import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, Loader2, Network } from 'lucide-react';
import { toast } from 'sonner';
import { usePartnerAuth } from '../../contexts/PartnerAuthContext';

export default function PartnerLogin() {
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const { login } = usePartnerAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success('Welcome back!');
      navigate('/reseller/dashboard');
    } catch (err) {
      toast.error(err?.detail || err?.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2.5 mb-4">
            <div className="w-10 h-10 bg-[#516EFA] rounded-xl flex items-center justify-center">
              <Network className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <p className="font-manrope font-bold text-slate-900 text-base leading-tight">EDUVA</p>
              <p className="text-xs text-[#516EFA] font-medium">Partner Network</p>
            </div>
          </div>
          <h1 className="text-2xl font-outfit font-semibold text-slate-900">Partner Login</h1>
          <p className="text-sm text-slate-500 mt-1">Access your partner dashboard</p>
        </div>

        <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">
                Email Address
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
                placeholder="partner@example.com"
                required
                className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] outline-none transition"
                data-testid="partner-login-email"
              />
            </div>
            <div>
              <label className="text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">
                Password
              </label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm(p => ({ ...p, password: e.target.value }))}
                placeholder="Your password"
                required
                className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] outline-none transition"
                data-testid="partner-login-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#516EFA] hover:bg-[#425BE0] text-white rounded-lg py-2.5 text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="partner-login-submit"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? 'Logging in...' : 'Login to Partner Portal'}
            </button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-4">
            New partner?{' '}
            <Link to="/reseller/register" className="text-[#516EFA] hover:text-[#425BE0] font-medium">
              Join the network
            </Link>
          </p>
        </div>

        <p className="text-center text-xs text-slate-400 mt-4">
          <a href="https://www.myeduva.com" className="hover:text-slate-600 transition-colors">← Back to Home</a>
        </p>
      </div>
    </div>
  );
}
